import { useCallback, useEffect, useState } from 'react';
import Spinner from '../../components/Spinner';
import { fosError, fosFunds, formatDate, formatMoney, isVersionConflict, type LedgerView, type PaymentView, type PledgeView } from '../../lib/fosApi';
import ContributionDrawer, { type ContributionMode } from './ContributionDrawer';
import { Kpi, ProgressBar } from './Progress';
import { useFinancialGuard } from './useFinancial';
import { EmptyState, ErrorNote, Icon, Section } from './ui';

const STATUS_CLASS: Record<PaymentView['status'], string> = { confirmed: 'status-active', reported: 'next', cancelled: 'off' };
const STATUS_TEXT: Record<PaymentView['status'], string> = { confirmed: 'Confirmed', reported: 'Awaiting confirmation', cancelled: 'Cancelled' };

/** Asks for the reason a pledge or gift is being cancelled (the record is kept and the reason is audited). */
function ReasonModal({ title, onCancel, onConfirm }: { title: string; onCancel: () => void; onConfirm: (reason: string) => Promise<void> }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const go = async () => { setBusy(true); setError(''); try { await onConfirm(reason.trim()); } catch (cause) { setError(fosError(cause).message); setBusy(false); } };
  return (
    <div className="fos-modal-backdrop" role="presentation">
      <div className="fos-modal" role="dialog" aria-modal="true" aria-labelledby="fos-reason-title">
        <h2 id="fos-reason-title">{title}</h2>
        <p>The record stays in the ledger marked cancelled. Say why, so the history makes sense.</p>
        <textarea className="fos-summary-edit" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} placeholder="Reason" autoFocus />
        <ErrorNote message={error} />
        <div className="fos-actions"><button className="btn" onClick={onCancel} disabled={busy}>Keep it</button><button className="btn primary" onClick={go} disabled={busy || !reason.trim()}>{busy ? <Spinner /> : 'Cancel record'}</button></div>
      </div>
    </div>
  );
}

function PledgeRow({ p, onGift, onCancel }: { p: PledgeView; onGift: () => void; onCancel: () => void }) {
  return (
    <li className={`fos-ledger-row${p.status === 'cancelled' ? ' cancelled' : ''}`}>
      <div className="fos-ledger-main">
        <div className="fos-ledger-title"><strong>{formatMoney(p.amountCents)}</strong> <span className="fos-ref">{p.ref}</span>
          <span className="fos-chip scope">{p.scopeLabel}</span>{p.status === 'cancelled' && <span className="fos-chip off">Cancelled</span>}</div>
        <div className="subtitle">{p.campaignName} · {p.campaignYear}</div>
        {p.status === 'active' && (
          <>
            <ProgressBar raised={p.confirmedCents} pending={p.paidCents - p.confirmedCents} outstanding={p.outstandingCents} goal={null} />
            <div className="fos-ledger-foot">
              <span>{formatMoney(p.paidCents)} received</span>
              <strong className={p.outstandingCents > 0 ? 'owed' : 'paid'}>{p.outstandingCents > 0 ? `${formatMoney(p.outstandingCents)} outstanding` : 'Paid in full'}</strong>
            </div>
          </>
        )}
      </div>
      {p.status === 'active' && (
        <div className="fos-ledger-actions">
          {p.outstandingCents > 0 && <button className="btn small" onClick={onGift}><Icon name="plus" size={13} /> Gift</button>}
          {p.canCancel && <button className="linkbtn danger" onClick={onCancel}>Cancel</button>}
        </div>
      )}
    </li>
  );
}

function PaymentRow({ p, busy, onConfirm, onCancel }: { p: PaymentView; busy: boolean; onConfirm: () => void; onCancel: () => void }) {
  return (
    <li className={`fos-ledger-row compact${p.status === 'cancelled' ? ' cancelled' : ''}`}>
      <div className="fos-ledger-main">
        <div className="fos-ledger-title"><strong>{formatMoney(p.amountCents)}</strong> <span className="fos-ref">{p.ref}</span><span className={`fos-chip ${STATUS_CLASS[p.status]}`}>{STATUS_TEXT[p.status]}</span></div>
        <div className="subtitle">{formatDate(p.receivedDate)} · {p.campaignName} {p.campaignYear} · {p.scopeLabel}{p.pledgeRef ? ` · toward ${p.pledgeRef}` : ' · no pledge'}</div>
      </div>
      <div className="fos-ledger-actions">
        {p.canConfirm && <button className="btn small primary" disabled={busy} onClick={onConfirm}><Icon name="check" size={13} /> Confirm</button>}
        {p.canCancel && <button className="linkbtn danger" disabled={busy} onClick={onCancel}>Cancel</button>}
      </div>
    </li>
  );
}

/** A donor's giving: pledges with their balances, and gifts. Shown only where the viewer may see financial history here. */
export default function LedgerPanel({ donorId, donorName }: { donorId: string; donorName: string }) {
  const { guard, modal } = useFinancialGuard();
  const [ledger, setLedger] = useState<LedgerView | null>(null);
  const [error, setError] = useState('');
  const [declined, setDeclined] = useState(false);
  const [drawer, setDrawer] = useState<ContributionMode | null>(null);
  const [cancelling, setCancelling] = useState<{ kind: 'pledge' | 'payment'; id: string; version: number } | null>(null);
  const [busyId, setBusyId] = useState('');

  const load = useCallback(async () => {
    setError(''); setDeclined(false);
    try { setLedger(await guard(() => fosFunds.ledger({ donorId }))); } catch (cause) { const c = fosError(cause); if (c.code === 'FOS_FINANCIAL_ACK_REQUIRED') setDeclined(true); else setError(c.message); }
  }, [donorId, guard]);
  useEffect(() => { void load(); }, [load]);

  const confirm = async (p: PaymentView) => {
    setBusyId(p.id); setError('');
    try { await guard(() => fosFunds.mutate({ operation: 'payment.confirm', paymentId: p.id, expectedVersion: p.version })); await load(); }
    catch (cause) { const c = fosError(cause); setError(isVersionConflict(c) ? 'This gift changed. Reloaded.' : c.message); await load(); } finally { setBusyId(''); }
  };

  const action = ledger && (ledger.pledgeScopes.length > 0 || ledger.paymentScopes.length > 0) ? (
    <div className="fos-actions">
      {ledger.pledgeScopes.length > 0 && <button className="btn small" onClick={() => setDrawer({ kind: 'pledge' })}><Icon name="plus" size={13} /> Pledge</button>}
      {ledger.paymentScopes.length > 0 && <button className="btn small primary" onClick={() => setDrawer({ kind: 'gift' })}><Icon name="plus" size={13} /> Gift</button>}
    </div>
  ) : null;

  return (
    <Section title="Giving" icon="dollar" action={action}>
      {declined && <p className="fos-hint"><Icon name="lock" size={13} /> Giving is shown after you acknowledge that your access to financial information is logged. <button className="linkbtn" onClick={() => void load()}>Show giving</button></p>}
      {!ledger && !error && !declined && <Spinner label="Loading giving" />}
      <ErrorNote message={error} />
      {ledger && (
        <>
          <div className="fos-kpis small">
            <Kpi label="Received (confirmed)" value={formatMoney(ledger.totals.confirmedCents)} tone="good" />
            <Kpi label="Awaiting confirmation" value={formatMoney(ledger.totals.reportedCents)} tone="gold" />
            <Kpi label="Pledged, outstanding" value={formatMoney(ledger.totals.outstandingCents)} />
          </div>
          {ledger.pledges.length === 0 && ledger.payments.length === 0 ? (
            <EmptyState icon="dollar" title="No giving recorded yet">Record a pledge or a gift to start this donor's history.</EmptyState>
          ) : (
            <>
              {ledger.pledges.length > 0 && <><h4 className="fos-subhead">Pledges</h4><ul className="fos-ledger">{ledger.pledges.map((p) => (
                <PledgeRow key={p.id} p={p} onGift={() => setDrawer({ kind: 'gift', pledgeId: p.id })} onCancel={() => setCancelling({ kind: 'pledge', id: p.id, version: p.version })} />))}</ul></>}
              {ledger.payments.length > 0 && <><h4 className="fos-subhead">Gifts</h4><ul className="fos-ledger">{ledger.payments.map((p) => (
                <PaymentRow key={p.id} p={p} busy={busyId === p.id} onConfirm={() => void confirm(p)} onCancel={() => setCancelling({ kind: 'payment', id: p.id, version: p.version })} />))}</ul></>}
            </>
          )}
          <p className="fos-privacy"><Icon name="lock" size={12} /> Financial information is confidential. You see only money credited to your own scopes, and each time it is shown it is logged.</p>
        </>
      )}
      {drawer && ledger && <ContributionDrawer mode={drawer} donor={{ id: donorId, name: donorName }} ledger={ledger} onClose={() => setDrawer(null)} onSaved={() => { setDrawer(null); void load(); }} />}
      {cancelling && (
        <ReasonModal title={cancelling.kind === 'pledge' ? 'Cancel this pledge?' : 'Cancel this gift?'} onCancel={() => setCancelling(null)} onConfirm={async (reason) => {
          await guard(() => (cancelling.kind === 'pledge'
            ? fosFunds.mutate({ operation: 'pledge.cancel', pledgeId: cancelling.id, expectedVersion: cancelling.version, reason })
            : fosFunds.mutate({ operation: 'payment.cancel', paymentId: cancelling.id, expectedVersion: cancelling.version, reason })));
          setCancelling(null); await load();
        }} />
      )}
      {modal}
    </Section>
  );
}
